import { tracking, useTracking } from '@podiumhq/tracking';

import {
  checkAndGetTargetValue,
  getErrorMessage,
  getTargetValue,
  capitalizeFirstLetter,
} from 'lib/fieldValidation';
import toTitleCase from 'lib/toTitleCase';

export const useFormField = ({
  customFieldValidation,
  customOnBlur,
  customUserProperty,
  errors = {},
  fieldName,
  isSelect,
  isUserProperty,
  omitTrackingValue,
  setErrors,
  setFieldValue,
  trackError,
  trackingEventName,
  trackingEventType = 'trials',
  trackingEventNamespace = 'signup',
} = {}) => {
  const { trackEvent } = useTracking();
  const trackingEventPath = `${trackingEventType}.${trackingEventName}`;

  const clearError = (fieldName) =>
    setErrors && setErrors({ ...errors, [fieldName]: null });

  const trackFieldEvent = (value) => {
    const eventValue = omitTrackingValue ? {} : { [fieldName]: value };
    trackEvent(`${trackingEventNamespace}.formFieldFilled`, {
      fieldName: `${trackingEventName}${capitalizeFirstLetter(fieldName)}`,
      value: omitTrackingValue ? null : value,
    });
    // TODO: deprecate in amplitude
    trackEvent(`${trackingEventPath}.${fieldName}FieldFilled`, eventValue);

    if (isUserProperty || customUserProperty) {
      const userProperty = customUserProperty || toTitleCase(fieldName);
      tracking.setUserProperties({ [userProperty]: value });
    }
  };

  const validateField = (value) => {
    const errorMessage = customFieldValidation
      ? customFieldValidation(value)
      : getErrorMessage(fieldName, value);
    const hasPreviousError = !!errors[fieldName];
    const updatedErrors =
      hasPreviousError || !!errorMessage
        ? {
            ...errors,
            [fieldName]: errorMessage || null,
          }
        : errors;

    setErrors && setErrors(updatedErrors);

    if (trackError && !!errorMessage) {
      trackEvent(`${trackingEventPath}.error`, {
        error: errorMessage,
        [fieldName]: value,
      });
    }
  };

  const handleOnBlur = async (event) => {
    if (
      event?.target?.ariaLabel === 'icon-button-EyeCrossed' ||
      event?.target?.ariaLabel === 'icon-button-Eye'
    )
      return;

    const value = getTargetValue(event);

    if (value) trackFieldEvent(value);

    if (!isSelect) validateField(value);

    if (customOnBlur) await customOnBlur(value);
  };

  const handleOnChange = (event) => {
    const value = checkAndGetTargetValue(event);

    if (isSelect) {
      validateField(value);
      trackFieldEvent(value);
    } else {
      clearError(fieldName);
    }
    setFieldValue && setFieldValue(value, fieldName);
  };

  return { handleOnBlur, handleOnChange };
};
